<template>
  <Teleport to="body">
    <!-- Backdrop -->
    <div
      class="fixed inset-0 z-[213425459999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
      @click="handleBackdropClick"
    >
      <!-- Container -->
      <div
        class="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in-95 slide-in-from-top-4 duration-300"
        @click.stop
      >
        <!-- Header -->
        <div
          class="flex items-center justify-between p-6 border-b border-gray-100"
        >
          <div class="flex items-center gap-4">
            <div
              class="w-12 h-12 rounded-xl bg-blue-600 flex items-center justify-center text-white shadow-lg shadow-blue-200"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path
                  d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"
                />
                <polyline points="14,2 14,8 20,8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div>
              <h2 class="text-xl font-bold text-gray-900 leading-tight">
                Export Details
              </h2>
              <p class="text-sm font-medium text-gray-500 mt-0.5">
                {{ exportData.title }}
              </p>
            </div>
          </div>
          <button
            @click="$emit('close')"
            class="p-2 rounded-full hover:bg-gray-100 text-gray-400 hover:text-rose-600 transition-colors"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div
          class="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-thin scrollbar-thumb-gray-200"
        >
          <!-- General Info Section -->
          <section>
            <div class="flex items-center gap-2 mb-4 text-emerald-600">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path
                  d="M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z"
                />
                <polyline points="13,2 13,9 20,9" />
              </svg>
              <h3
                class="font-bold uppercase tracking-wider text-xs text-gray-700"
              >
                General Information
              </h3>
            </div>
            <div
              class="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 rounded-xl p-3 border border-gray-100"
            >
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter"
                  >File Name</label
                >
                <span class="text-sm font-semibold text-gray-800">{{
                  exportData.title
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter"
                  >Type</label
                >
                <span class="text-xs font-bold text-emerald-600 uppercase">{{
                  exportData.type
                }}</span>
              </div>
              <div class="flex flex-col gap-1 md:col-span-2">
                <label
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter"
                  >Description</label
                >
                <span class="text-sm text-gray-600">{{
                  exportData.description || "No description provided"
                }}</span>
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter"
                  >Status</label
                >
                <span
                  :class="[
                    'px-3 py-1 rounded-full text-[10px] font-bold uppercase w-fit shadow-sm',
                    getStatusClass(exportData.status),
                  ]"
                >
                  {{ exportData.status }}
                </span>
              </div>
              <div class="flex flex-col gap-1">
                <label
                  class="text-[10px] font-bold text-gray-400 uppercase tracking-tighter"
                  >Archived</label
                >
                <span class="text-sm font-medium text-gray-700">{{
                  exportData.is_archived ? "Yes" : "No"
                }}</span>
              </div>
            </div>
          </section>

          <!-- Timeline Section -->
          <section>
            <div class="flex items-center gap-2 mb-4 text-blue-600">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <circle cx="12" cy="12" r="10" />
                <polyline points="12,6 12,12 16,14" />
              </svg>
              <h3
                class="font-bold uppercase tracking-wider text-xs text-gray-700"
              >
                Timeline
              </h3>
            </div>
            <div class="flex flex-wrap gap-8 px-2">
              <div class="flex items-center gap-3">
                <div
                  class="w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-emerald-50"
                ></div>
                <div class="flex flex-col">
                  <label class="text-[10px] font-bold text-gray-400 uppercase"
                    >Created</label
                  >
                  <span class="text-sm font-medium text-gray-700">{{
                    formattedDate(exportData.created_at)
                  }}</span>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="w-3 h-3 rounded-full bg-blue-500 ring-4 ring-blue-50"
                ></div>
                <div class="flex flex-col">
                  <label class="text-[10px] font-bold text-gray-400 uppercase"
                    >Last Updated</label
                  >
                  <span class="text-sm font-medium text-gray-700">{{
                    formattedDate(exportData.updated_at)
                  }}</span>
                </div>
              </div>
            </div>
          </section>

          <!-- Columns Section -->
          <section v-if="exportData.columns?.length">
            <div class="flex items-center gap-2 mb-4 text-gray-600">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path d="M3 6h18" />
                <path d="M3 12h18" />
                <path d="M3 18h18" />
              </svg>
              <h3
                class="font-bold uppercase tracking-wider text-xs text-gray-700"
              >
                Exported Columns
              </h3>
            </div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="(column, index) in exportData.columns"
                :key="index"
                class="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-[11px] font-semibold text-gray-600 shadow-sm"
              >
                {{ formatColumn(column) }}
              </span>
            </div>
          </section>

          <!-- Filters Section -->
          <section
            v-if="exportData.filters && Object.keys(exportData.filters).length"
          >
            <div class="flex items-center gap-2 mb-4 text-amber-600">
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <polygon points="22,3 2,3 10,12.46 10,19 14,21 14,12.46 22,3" />
              </svg>
              <h3
                class="font-bold uppercase tracking-wider text-xs text-gray-700"
              >
                Active Filters
              </h3>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div
                v-for="(value, key) in exportData.filters"
                :key="key"
                class="flex items-center justify-between p-3 border border-gray-100 rounded-xl bg-amber-50/30"
              >
                <span class="text-[10px] font-bold text-amber-700 uppercase">{{
                  formatColumn(key)
                }}</span>
                <span class="text-sm font-semibold text-gray-700">{{
                  value
                }}</span>
              </div>
            </div>
          </section>

          <!-- Download Action -->
          <section v-if="exportData.path">
            <div
              @click="downloadResource(exportData)"
              class="group cursor-pointer flex items-center justify-between p-5 bg-emerald-600 rounded-xl text-white shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all active:scale-[0.98]"
            >
              <div class="flex items-center gap-4">
                <div class="p-2 bg-white/20 rounded-lg">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                </div>
                <div>
                  <h4 class="font-bold">Ready to Download</h4>
                  <p class="text-emerald-100 text-xs">
                    Click here to save the file locally
                  </p>
                </div>
              </div>
              <svg
                class="w-5 h-5 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </div>
          </section>
        </div>

        <!-- Footer -->
        <div class="p-4 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button
            @click="$emit('close')"
            class="px-6 py-2 bg-white border border-gray-200 text-gray-700 text-sm font-bold rounded-xl hover:bg-gray-50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { formatString } from "@/helpers/date";

export default {
  name: "ExportDetailsModal",
  props: {
    exportData: { type: Object, required: true },
    downloadResource: Function,
  },
  emits: ["close"],
  methods: {
    getStatusClass(status) {
      const s = status?.toLowerCase();
      if (s === "pending")
        return "bg-amber-100 text-amber-700 border border-amber-200";
      if (s === "authorized" || s === "completed")
        return "bg-emerald-100 text-emerald-700 border border-emerald-200";
      if (s === "rejected")
        return "bg-rose-100 text-rose-700 border border-rose-200";
      return "bg-gray-100 text-gray-600 border border-gray-200";
    },
    formatColumn(col) {
      if (col === "uuid") return "Bet ID";
      if (col === "user_id") return "Player ID";
      const spaced = col.replace(/([a-z])([A-Z])/g, "$1 $2");
      const clean = spaced.replace(/_/g, " ");
      return clean
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ");
    },
    handleBackdropClick() {
      this.$emit("close");
    },
    handleKeydown(event) {
      if (event.key === "Escape") this.$emit("close");
    },
    formattedDate(date) {
      return formatString(date);
    },
  },
  mounted() {
    document.addEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "hidden";
  },
  unmounted() {
    document.removeEventListener("keydown", this.handleKeydown);
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
/* Utility per la scrollbar */
.scrollbar-thin::-webkit-scrollbar {
  width: 4px;
}
.scrollbar-thin::-webkit-scrollbar-track {
  background: transparent;
}
.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #e5e7eb;
  border-radius: 10px;
}
</style>

 
